import React, { createRef, useRef, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Form,
  Row,
  Col,
  Input,
  Select,
  Button,
  Checkbox,
  Tooltip,
  notification,
} from "antd";
import { InputMask } from "@mtm/components";
import commonStyles from "./PersonForm.module.scss";
import styles from "./ProfileForm.module.scss";
import PersonAddress from "./PersonAddress";
import PersonPhoto from "./PersonPhoto";
import PersonAccommodations from "./PersonAccommodations";
import { selectDropdownArrowIcon } from "../../../assets/images";
import { isEmailValid } from "../../../helpers/ValidationHelpers";
import PersonDateOfBirth from "./PersonDateOfBirth";
import { useServiceUserCreationCtx } from "../../../context/ServiceUserCreationContext";
import {
  AddressRef,
  UspsAddresses,
  defaultSelectedAddress,
} from "../../../types/LocationTypes";
import { useProfileActions } from "../../../hooks/useProfileActions";
import UnsavedChangesModal from "../Modals/UnsavedChangesModal";
import InvalidEmailModal from "../Modals/InvalidEmailModal";
import { usePlanDetailsCtx } from "../../../context/PlanDetailsContext";
import { PersonInitialValues } from "../../../types/PersonTypes";
import UspsAddressModal from "../Modals/UspsAddressModal";
import { useUspsValidator } from "../../../hooks/useUspsValidator";

type Props = {
  initialValues: PersonInitialValues;
};

function ProfileForm({ initialValues }: Props) {
  const [
    isUnsavedChangesModalVisible,
    setIsUnsavedChangesModalVisible,
  ] = useState<boolean>(false);
  const [isInvalidEmailModalVisible, setIsInvalidEmailModalVisible] = useState<
    boolean
  >(false);

  const { personId } = useParams<{ personId: string }>();
  const history = useHistory();

  const [form] = Form.useForm();

  const { relationshipTypesCtx } = useServiceUserCreationCtx();
  const [relationshipTypes] = relationshipTypesCtx;

  const { tripDetailsInfo } = usePlanDetailsCtx();

  function navigateOnSubmitForm() {
    history.goBack();
  }

  const {
    shouldDisableBtn,
    validateForm,
    profileInfo,
    handleSubmitForm,
    isLoading,
  } = useProfileActions(
    form,
    Number(personId),
    initialValues,
    setIsInvalidEmailModalVisible,
    navigateOnSubmitForm
  );

  const {
    isCaregiverProfile,
    isActiveServiceUser,
    isEmailDisabled,
  } = profileInfo;

  const {
    addressRef,
    uspsAddresses,
    handleUspsValidationAndSubmitForm,
    isUspsAddressModalVisible,
    setIsUspsAddressModalVisible,
  } = useUspsValidator(form, handleSubmitForm);

  const labels = {
    title: isCaregiverProfile ? "Edit Profile" : "Edit Service User",
    address: {
      cantFindAddressBtnTxt: `Can't find ${
        isCaregiverProfile ? "your" : "their"
      } address?`,
    },
    accommodations: {
      wheelChair: "Does this person travel with a wheelchair?",
      standardFoldableWheelchair:
        "Does this person have a standard, foldable wheelchair?",
      transferableWheelchair:
        "Is this person able to transfer from a wheelchair to a vehicle?",
    },
  };

  function handleValuesChange() {
    validateForm();
  }

  function handleIsSameAsHomeAddress(isChecked: boolean) {
    if (isChecked) {
      const address = form.getFieldValue("homeAddress");
      form.setFieldsValue({
        mailingAddress: address,
      });
    } else {
      form.setFieldsValue({
        mailingAddress: defaultSelectedAddress,
      });
    }
    validateForm();
  }

  function handleCancel() {
    setIsUnsavedChangesModalVisible(true);
  }

  return (
    <>
      <Form
        form={form}
        name="profileForm"
        layout="vertical"
        initialValues={initialValues}
        onValuesChange={handleValuesChange}
        onFinish={handleUspsValidationAndSubmitForm}
        requiredMark={false}
        validateTrigger={["onBlur"]}
      >
        <Row>
          <Col span={24} className={styles.title}>
            {labels.title}
          </Col>
          <Col span={24} className={styles.userName}>
            {initialValues?.firstName} {initialValues?.lastName}
          </Col>

          <Col xs={24} lg={12}>
            <Row className={commonStyles.mobileFormAlign}>
              <Col span={24}>
                <Form.Item
                  name="phone"
                  label={<div className={commonStyles.label}>Phone Number</div>}
                  className={styles.formItemAlign}
                >
                  <InputMask
                    className={commonStyles.input}
                    mask="999-999-9999"
                    maskPlaceholder=""
                    placeholder="Phone Number"
                    onChange={() => {}}
                  />
                </Form.Item>
              </Col>

              <Col span={24} className={commonStyles.label}>
                Email Address
              </Col>
              <Col span={24}>
                <Tooltip
                  title={
                    isEmailDisabled && (
                      <div>
                        Please contact us at{" "}
                        {tripDetailsInfo.wandaCustomerCareNumber} to change this
                        information.
                      </div>
                    )
                  }
                  overlayClassName={styles.tooltip}
                >
                  <Form.Item
                    name="email"
                    rules={[
                      { required: false, message: "" },
                      {
                        validator(_, value) {
                          if (!value || isEmailValid(value)) {
                            return Promise.resolve();
                          }
                          return Promise.reject("");
                        },
                      },
                    ]}
                    className={`${!isCaregiverProfile &&
                      commonStyles.formItemMargin} ${styles.formItemAlign}`}
                  >
                    <Input
                      placeholder="Email"
                      className={`${commonStyles.input} ${isEmailDisabled &&
                        commonStyles.disabledInput}`}
                      disabled={isEmailDisabled}
                    />
                  </Form.Item>
                </Tooltip>

                {!isCaregiverProfile && (
                  <Row gutter={[0, 10]}>
                    <Col className={commonStyles.optional}>**Optional</Col>
                    <Col span={24} />
                  </Row>
                )}
              </Col>

              <PersonDateOfBirth form={form} isEditProfile={true} />

              {!isCaregiverProfile && (
                <Col span={24}>
                  <Form.Item
                    name="relationship"
                    label={
                      <div className={commonStyles.label}>Relationship</div>
                    }
                    className={styles.formItemAlign}
                  >
                    <Select
                      className={commonStyles.relationshipSelect}
                      placeholder="Relationship"
                      suffixIcon={
                        <img
                          src={selectDropdownArrowIcon}
                          alt="select_reason_dropdown"
                        />
                      }
                      options={relationshipTypes}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
          </Col>

          <Col xs={24} lg={12}>
            <Row className={commonStyles.mobileFormAlign}>
              <Col span={24} className={commonStyles.label}>
                Home Address
              </Col>
              <Col>
                <PersonAddress
                  ref={addressRef.current[0]}
                  form={form}
                  labels={labels.address}
                  addressType="homeAddress"
                  validateAndCheckForm={validateForm}
                  shouldValidateZip={isActiveServiceUser}
                />
              </Col>

              <Col span={24} className={commonStyles.label}>
                Mailing Address
              </Col>
              <Col span={24}>
                <Row gutter={[10, 10]} align="middle">
                  <Col>
                    <Form.Item
                      name="isSameAsHomeAddress"
                      className={commonStyles.formItemMargin}
                      valuePropName="checked"
                    >
                      <Checkbox
                        className={commonStyles.checkbox}
                        onChange={(e) =>
                          handleIsSameAsHomeAddress(e.target.checked)
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col className={commonStyles.checkboxLabel}>
                    Same as home address
                  </Col>
                  <Col span={24} />
                </Row>
              </Col>

              <Col span={24}>
                <Form.Item
                  className={commonStyles.formItemMargin}
                  dependencies={["isSameAsHomeAddress"]}
                >
                  {({ getFieldValue }) =>
                    !getFieldValue("isSameAsHomeAddress") && (
                      <PersonAddress
                        ref={addressRef.current[1]}
                        form={form}
                        labels={labels.address}
                        addressType="mailingAddress"
                        validateAndCheckForm={validateForm}
                        shouldValidateZip={isActiveServiceUser}
                      />
                    )
                  }
                </Form.Item>
              </Col>

              <Col span={24}>
                <Row gutter={[5, 0]}>
                  <Col className={commonStyles.label}>Profile Photo</Col>
                  <Col className={commonStyles.optional}>**Optional</Col>
                </Row>

                <PersonPhoto form={form} />
              </Col>

              {isActiveServiceUser && (
                <>
                  <Col span={24} className={commonStyles.label}>
                    Accommodations
                  </Col>
                  <Col span={24}>
                    <PersonAccommodations
                      form={form}
                      labels={labels.accommodations}
                      accommodationType="wandaAccommodations"
                    />
                  </Col>
                </>
              )}

              <Col span={24} className={styles.formBtnAlign}>
                <Button
                  className={styles.cancel}
                  type="primary"
                  onClick={handleCancel}
                  htmlType="button"
                >
                  Cancel
                </Button>
                <Button
                  className={`${styles.saveChanges} ${shouldDisableBtn &&
                    commonStyles.disableBtn}`}
                  type="primary"
                  htmlType="submit"
                  disabled={shouldDisableBtn}
                  loading={isLoading}
                >
                  Save Changes
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>

      <UnsavedChangesModal
        visible={isUnsavedChangesModalVisible}
        setVisibility={setIsUnsavedChangesModalVisible}
      />

      <InvalidEmailModal
        visible={isInvalidEmailModalVisible}
        setVisibility={setIsInvalidEmailModalVisible}
      />

      <UspsAddressModal
        form={form}
        uspsAddresses={uspsAddresses.current}
        visible={isUspsAddressModalVisible}
        setVisibility={setIsUspsAddressModalVisible}
        handleSubmitForm={handleSubmitForm}
      />
    </>
  );
}

export default ProfileForm;
